<template>
  <Teleport to="body">
    <div
      v-if="isOpen && imagesIds.length > 0"
      class="root"
    >
      <div class="dialog">
        <div class="dialog__head">
          <div class="dialog__title">
            {{ title }}
          </div>
          <div
            class="dialog__description"
            v-html="description"
          >
          </div>
        </div>

        <div class="dialog__body pop-up-gallery">
          <swiper
            class="swiper"
            :modules="[Navigation]"
            :centered-slides="true"
            :max-backface-hidden-slides="1"
            :navigation="swiperNavigation"
            :initial-slide="indexActiveSlide"
            @slideChange="slideChangeTransitionEnd"
            @swiper="getMainSliderRef"
          >
            <swiper-slide
              v-for="(imageId, index) in imagesIds"
              :key="`slider-image-${index}-${imageId}`"
              class="swiper-slide"
            >
              <div class="swiper-slide__content">
                <nuxt-img
                  loading="lazy"
                  :src="String(imageId)"
                  height="680"
                  fit="fill"
                />
              </div>
            </swiper-slide>

            <div
              slot="button-prev"
              class="product-slider-images__button swiper-button-prev popup-gallery-swiper-button popup-gallery-swiper-button-prev"
            />
            <div
              slot="button-next"
              class="product-slider-images__button swiper-button-next popup-gallery-swiper-button popup-gallery-swiper-button-next"
            />
          </swiper>
        </div>

        <div class="dialog__footer">
          <div class="dialog__pagination">
            <div
              v-for="(imageId, index) in imagesIds"
              :key="`image-${ index }-${ imageId }`"
              :class="{
                'active': Boolean(index === active)
              }"
              @click="() => slideSetIndex(index)"
            >
              <nuxt-img
                width="60"
                height="40"
                :src="String(imageId)"
              />
            </div>
          </div>
        </div>

        <div
          class="dialog__close"
          @click="closePopup"
        >
          <img
            :src="CloseIcon"
            alt="close icon"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import 'swiper/css/bundle';
import { ref, watch } from '#imports';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';
import type { NavigationOptions, Swiper as SwiperClass } from 'swiper/types';
import CloseIcon from '#sitis/assets/images/svg/common/close-white.svg';
import usePopupGallery from '#sitis/composables/useSitisPopupGallery';

const {
  title,
  imagesIds,
  isOpen,
  description,
  indexActiveSlide,
  setStateValue
} = usePopupGallery();

const swiperNavigation = ref<NavigationOptions | boolean>({
  nextEl: '.swiper-button-next',
  prevEl: '.swiper-button-prev'
});
const active = ref<number>(indexActiveSlide.value);
const mainSliderRef = ref<SwiperClass | null>(null);

const getMainSliderRef = (swiperInstance: SwiperClass) => {
  mainSliderRef.value = swiperInstance;
};

const slideChangeTransitionEnd = () => {
  let activeIndex = 0;

  if (mainSliderRef.value) {
    activeIndex = mainSliderRef.value.activeIndex;
  }

  active.value = activeIndex;
};

const slideSetIndex = (index: number) => {
  if (mainSliderRef.value) {
    mainSliderRef.value.slideTo(index);
  }
};

const closePopup = () => {
  setStateValue({
    isOpen: false
  });
};

const open = () => {
  document.body.style.cssText = 'overflow: hidden';
};

const close = () => {
  document.body.style.cssText = '';
};

watch(isOpen, (newIsOpen) => {
  if (newIsOpen) {
    open();
  } else {
    close();
  }
});

</script>

<style>
.root {
  box-sizing: border-box;
  position: fixed;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 999;
}
.root::after {
  content: "";
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  background: var(--popup-gallery-overlay);
  backdrop-filter: blur(20px);
}

.dialog {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  z-index: 20;
  padding: 26px 62px;
}

.dialog__head {
  margin: 0 auto;
}

.dialog__body {
  height: 100%;
  max-height: calc(100vh - 200px);
  position: relative;
  margin-top: 30px;
}

.dialog__footer {
  margin-top: 20px;
}

.dialog__title {
  box-sizing: border-box;
  font-size: 36px;
  line-height: 43px;
  text-align: center;
  font-feature-settings: "pnum" on, "lnum" on;
  color: #fff;
  margin-bottom: 12px;
}

.dialog__description {
  box-sizing: border-box;
  font-size: 16px;
  line-height: normal;
  text-align: center;
  font-feature-settings: "pnum" on, "lnum" on;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
}
.dialog__description > * {
  font-size: 16px;
  line-height: normal;
  font-feature-settings: "pnum" on, "lnum" on;
  margin: 0 0 5px;
}

.dialog__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -4px;
}
.dialog__pagination > * {
  position: relative;
  overflow: hidden;
  width: 60px;
  height: 40px;
  border-radius: 2px;
  margin-left: 4px;
  cursor: pointer;
}
.dialog__pagination > * img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.dialog__pagination > *.active::after {
  content: none;
}
.dialog__pagination > *::after {
  content: "";
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 5;
  left: 0;
  top: 0;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6));
}

.dialog__close {
  position: absolute;
  right: 20px;
  top: 20px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.dialog__close:hover {
  transform: rotate(180deg);
}
</style>
<style lang="scss">
.pop-up-gallery {
	.swiper-container {
		height: 100%;
	}

	.swiper-wrapper {
		max-width: 1300px;
		max-height: calc(100vh - 170px);
		margin: 0 auto;
	}

	.swiper-slide {
		height: 100%;
		display: flex;
		align-items: center;
	}

	.swiper-slide img {
		border-radius: 6px;
		max-height: 100%;

		//object-fit: contain;
	}

	.product-slider-images__button,
	.popup-gallery-swiper-button,
	.popup-gallery-swiper-button.popup-gallery-swiper-button-prev,
	.popup-gallery-swiper-button.popup-gallery-swiper-button-next {
		box-sizing: border-box;
		width: 48px;
		height: 48px;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 1px solid white;
		border-radius: 100%;

		&::after {
			font-size: 20px;
			color: white;
		}
	}

	.swiper-slide__content {
		width: 100%;
		height: 100%;
		max-width: 1300px;
		display: flex;
		align-items: center;
		justify-content: center;
	}
}
</style>
